// ChartTower Corp. Linktree Page
// Pagina stile Linktree con tutti i link aziendali principali

import React from 'react';
import { ExternalLink, Users, Globe, Send, Twitter, Instagram } from 'lucide-react';
import { COMPANY_INFO, EXTERNAL_LINKS, ASSETS } from '../constants';
import { LanguageSwitcher } from '../components/shared/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';

interface LinkButtonProps {
    icon: React.ReactNode;
    label: string;
    url: string;
    description?: string;
}

const LinkButton: React.FC<LinkButtonProps> = ({ icon, label, url, description }) => {
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="group w-full max-w-2xl mx-auto block"
        >
            <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-2xl p-5 hover:border-blue-500/50 hover:from-blue-900/20 hover:to-blue-800/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                            {icon}
                        </div>
                        <div className="text-left">
                            <h3 className="text-white font-bold text-lg group-hover:text-blue-400 transition-colors uppercase">
                                {label}
                            </h3>
                            {description && (
                                <p className="text-gray-400 text-sm mt-0.5">{description}</p>
                            )}
                        </div>
                    </div>
                    <ExternalLink className="text-gray-500 group-hover:text-blue-400 transition-colors" size={20} />
                </div>
            </div>
        </a>
    );
};

export const LinktreePage: React.FC = () => {
    const { t } = useLanguage();

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#050508] via-[#0A0E1A] to-[#050508] text-white relative overflow-hidden">
            {/* Language Switcher - Posizione Assoluta a Destra */}
            <div className="absolute top-6 right-6 z-50">
                <LanguageSwitcher />
            </div>

            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: '60px 60px'
                }} />
            </div>

            <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
                {/* Header Section */}
                <div className="text-center mb-12 animate-fade-in w-full max-w-4xl mx-auto">
                    {/* Logo - Large & Centered with Animations */}
                    <div className="mb-8 flex justify-center">
                        <div className="relative w-48 h-48 sm:w-56 sm:h-56">
                            {/* Animated Outer Ring - Rotating */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/40 to-blue-600/40 rounded-full animate-spin-slow blur-sm"></div>

                            {/* Pulsing Ring */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-blue-600/30 rounded-full animate-pulse"></div>

                            {/* Main Logo Container */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-blue-600/30 rounded-full p-[1px] shadow-[0_0_50px_rgba(59,130,246,0.4)] hover:shadow-[0_0_80px_rgba(59,130,246,0.6)] transition-all duration-500 hover:scale-110 group">
                                <div className="w-full h-full bg-[#0A0E1A] rounded-full flex items-center justify-center overflow-hidden relative">
                                    {/* Animated Glow Background */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-blue-600/10 group-hover:from-blue-500/20 group-hover:to-blue-600/20 transition-all duration-700"></div>

                                    {/* Logo Image */}
                                    <img
                                        src={ASSETS.LOGO_COMPANY}
                                        alt="ChartTower Corp."
                                        className="w-full h-full object-cover scale-110 transform transition-transform duration-700 group-hover:scale-125 group-hover:rotate-3"
                                    />

                                    {/* Shine Effect on Hover */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/0 to-transparent group-hover:via-white/10 transition-all duration-1000 opacity-0 group-hover:opacity-100 group-hover:translate-x-full" style={{ transform: 'translateX(-100%)' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Company Name */}
                    <h1 className="text-4xl sm:text-6xl font-black mb-4">
                        <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent drop-shadow-sm">
                            ChartTower Corp. OÜ
                        </span>
                    </h1>

                    <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto mb-8 font-light italic">
                        {t('linktree.subtitle')}
                    </p>

                    <div className="inline-flex items-center gap-2 px-6 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium tracking-wide">
                        <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse shadow-[0_0_10px_#3b82f6]"></div>
                        <span>{t('linktree.officialChannels')}</span>
                    </div>
                </div>

                {/* Links Section */}
                <div className="w-full max-w-xl space-y-4 mb-8">
                    <LinkButton
                        icon={<Users size={24} />}
                        label={t('linktree.corporateGroup')}
                        url={EXTERNAL_LINKS.TELEGRAM_ACCESS}
                        description={t('linktree.corporateGroupDesc')}
                    />

                    <LinkButton
                        icon={<Globe size={24} />}
                        label={t('linktree.website')}
                        url={window.location.origin}
                        description={t('linktree.websiteDesc')}
                    />

                    <LinkButton
                        icon={<Send size={24} />}
                        label={t('linktree.tokenChannel')}
                        url={EXTERNAL_LINKS.TELEGRAM_TOKEN_CHANNEL}
                        description={t('linktree.tokenChannelDesc')}
                    />

                    <LinkButton
                        icon={<Twitter size={24} />}
                        label={t('linktree.twitter')}
                        url={COMPANY_INFO.social.twitter}
                        description={t('linktree.twitterDesc')}
                    />

                    <LinkButton
                        icon={<Instagram size={24} />}
                        label={t('linktree.instagram')}
                        url={COMPANY_INFO.social.instagram}
                        description={t('linktree.instagramDesc')}
                    />
                </div>

                {/* Footer */}
                <div className="text-center text-gray-500 text-xs sm:text-sm">
                    <p>© 2025 www.ctc-corp.eu. {t('linktree.rightsReserved')}</p>
                </div>
            </div>

            {/* Decorative Elements */}
            <div className="fixed top-0 left-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] -z-10 animate-pulse"></div>
            <div className="fixed bottom-0 right-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px] -z-10 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
    );
};

